import type TurndownService from 'turndown'

export const addUnderlineRule = (turndown: TurndownService): void => {
  turndown.addRule('underline', {
    filter: ['u'],
    replacement: (content) => `<u>${content}</u>`,
  })
}

export const addStrikethroughRule = (turndown: TurndownService): void => {
  turndown.addRule('strikethrough', {
    filter: (node) =>
      node.nodeName === 'S' || node.nodeName === 'STRIKE' || node.nodeName === 'DEL',
    replacement: (content) => `~~${content}~~`,
  })
}

const isTaskListItem = (node: HTMLElement): boolean =>
  node.nodeName === 'LI' &&
  node.parentNode?.nodeName === 'UL' &&
  node.querySelector('input[type="checkbox"]') !== null

export const addTaskListRule = (turndown: TurndownService): void => {
  turndown.addRule('taskListItem', {
    filter: (node) => isTaskListItem(node as HTMLElement),
    replacement: (content, node) => {
      const checkbox = (node as HTMLElement).querySelector('input[type="checkbox"]')
      const isChecked = checkbox?.hasAttribute('checked') ?? false
      const cleanContent = content.replace(/^\s*\[[ x]\]\s*/i, '').trim()
      return `- [${isChecked ? 'x' : ' '}] ${cleanContent}\n`
    },
  })
}

export const addMentionRule = (turndown: TurndownService): void => {
  turndown.addRule('mention', {
    filter: (node) =>
      node.nodeName === 'SPAN' && (node as HTMLElement).getAttribute('data-type') === 'mention',
    replacement: (_content, node) => {
      const element = node as HTMLElement
      const label = element.getAttribute('data-label') ?? ''
      const href = element.getAttribute('data-href')
      const id = element.getAttribute('data-id') ?? ''
      if (href) {
        return `[@${label}](${href})`
      }
      return `@${label}<!-- mention:${id} -->`
    },
  })
}

export const addHighlightRule = (turndown: TurndownService): void => {
  turndown.addRule('highlight', {
    filter: ['mark'],
    replacement: (content) => `==${content}==`,
  })
}

export const addCodeRule = (turndown: TurndownService): void => {
  turndown.addRule('code', {
    filter: ['code'],
    replacement: (content) => `\`${content}\``,
  })
}

const convertTableRowToMarkdown = (row: HTMLTableRowElement): string => {
  const cells = Array.from(row.querySelectorAll('th, td'))
  const cellContents = cells.map((cell) => cell.textContent?.trim() ?? '')
  return `| ${cellContents.join(' | ')} |`
}

const createTableSeparator = (row: HTMLTableRowElement): string => {
  const cells = Array.from(row.querySelectorAll('th, td'))
  const separator = cells.map(() => '---').join(' | ')
  return `| ${separator} |`
}

export const addTableRule = (turndown: TurndownService): void => {
  turndown.addRule('table', {
    filter: 'table',
    replacement: (_content, node) => {
      const table = node as HTMLTableElement
      const rows = Array.from(table.querySelectorAll('tr'))
      if (rows.length === 0) return ''

      const result: string[] = []
      rows.forEach((row, rowIndex) => {
        result.push(convertTableRowToMarkdown(row))
        if (rowIndex === 0 && row.querySelector('th')) {
          result.push(createTableSeparator(row))
        }
      })
      return `\n${result.join('\n')}\n`
    },
  })
}
